import { useState, useEffect } from "react";
import { usePostHog } from "posthog-js/react";
import { FontSelector } from "../components/FontSelector";
import { PastAssetsGrid } from "../components/PastAssetsGrid";
import { LoadingMessage } from "../components/LoadingMessage";
import { mediaUrl } from "../mediaUrl";
import { EmailDesignModal } from "../components/EmailDesignModal";
import { usePageMeta } from "../usePageMeta";
import { FormatDownloadButtons } from "../components/FormatDownloadButtons";
import { DownsampleNotice } from "../components/DownsampleNotice";
import { ShareButton } from "../components/ShareButton";
import { ProUpsellCard } from "../components/ProUpsellCard";
import { EditorPromoCard } from "../components/EditorPromoCard";
import { recordExport } from "../digitizingServiceStorage";
import { useDownloadPrompts } from "../useDownloadPrompts";
import { type StoredTextSource } from "../editorStorage";
import { StudioLink } from "../components/StudioLink";

const PAGE_TITLE = "Embroidery Font Generator & PES File Maker – Free | Embroidery Online";
// Kept under ~160 characters -- see the matching note on HomePage.tsx.
const PAGE_DESCRIPTION =
  "Free PES file maker with 100+ embroidery fonts -- create embroidery text, preview the stitches, and export PES, DST, JEF, or EXP files, right in your browser.";

// Most common hoop sizes top out around 5" on a side. Above this the design
// is likely to be rejected or cropped by the machine, so it's worth flagging
// before someone downloads files they can't actually stitch.
const WIDE_HOOP_WARNING_INCHES = 5;

// GeneratedDesign.engine values this page's own grid can load back into the
// text-generation form (loadAssetToPreview needs asset.text/asset.font to
// mean typed text and a font name) -- mirrors media.py's
// ENGINE_DIGITIZER/ENGINE_INKSTITCH. A pastAssets entry from some other tool
// (app/routers/digitize.py's "svg_digitize") is real but not this page's to
// show -- see the cross-link rendered below the grid instead.
const CREATE_ENGINES = ["digitizer", "inkstitch"];

// Fields of a /api/generate response that aren't a format's download path.
// Every other key in it is one, so a new field has to be listed here or it
// turns up as a download button.
const GENERATE_RESPONSE_FIELDS = new Set([
  "png",
  "width_inches",
  "height_inches",
  "id",
  "size_warning",
  "stitches_removed",
]);

interface Font {
  name: string;
  preview: string;
  has_preview?: boolean;
}

interface AssetFile {
  [format: string]: string;
}

interface Asset {
  id: string;
  content_key: string;
  text: string;
  font: string;
  width_inches: number;
  height_inches: number;
  created_at: string;
  files: AssetFile;
  has_embroidery: boolean;
  is_unlocked: boolean;
  engine: string;
}

interface CreatePageProps {
  apiUrl: string;
  token: string;
  fonts: Font[];
  pastAssets: Asset[];
  onLoadAsset: (asset: Asset) => void;
  emailCaptureEnabled: boolean;
  stitchReplayerEnabled: boolean;
  formatConverterEnabled: boolean;
  embroideryViewerEnabled: boolean;
  shareButtonsEnabled: boolean;
  multilineEnabled: boolean;
  // Hydrated from /api/flags at page load, like every other flag
  // here -- "control" (the off/holdout state), "price_19", or "price_29".
  // Never read client-side via posthog.getFeatureFlag: see the flag read
  // in handleDownload below for why.
  proUpsellVariant: string;
}

interface FAQItem {
  question: string;
  answer: string;
}

// Deliberately distinct wording from the "What is an embroidery font
// generator?" section below, not a shorter copy of it -- a FAQ snippet and a
// body paragraph answering the same underlying question are expected to
// read differently, but the words themselves must not be the exact same
// paragraph repeated (see the file-level comment on duplication below).
const FAQ_DATA: FAQItem[] = [
  {
    question: "Can I create an embroidery file from text?",
    answer: "Yes. Type your text, choose a font, and generate -- you'll get a real embroidery file (PES, DST, JEF, or EXP).",
  },
  {
    question: "What is an embroidery font generator?",
    answer: "It's this tool -- type your text, pick a font, and it generates the stitch file.",
  },
  {
    question: "Can I make a PES file from text?",
    answer: "Yes, PES is one of the formats included with every design, along with DST, JEF, and EXP.",
  },
  {
    question: "What file formats do I get?",
    answer: "PES, DST, JEF, and EXP for anything you generate here. Need something else? Use the embroidery file converter.",
  },
  {
    question: "Can I change the size of my embroidery text?",
    answer: "Yes. Enter a width or height before generating, or leave both blank to use the font's original size.",
  },
  {
    question: "How do I preview my design before downloading?",
    answer: "Click Preview after entering your text and font. It shows the design and its dimensions before you generate or download anything.",
  },
];

// Module-level, not inline in JSX -- see the matching note on HomePage.tsx.
// FAQPage matches FAQ_DATA above rather than being hand-duplicated, same
// reasoning as HomePage.tsx's STRUCTURED_DATA: Google only credits FAQ
// structured data that matches text actually visible on the page.
const STRUCTURED_DATA: object[] = [
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ_DATA.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  },
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Embroidery Font Generator",
    url: "https://embroiderytext.com/create",
    description: PAGE_DESCRIPTION,
    applicationCategory: "DesignApplication",
    operatingSystem: "Any (runs in a web browser)",
    isAccessibleForFree: true,
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  },
];

// What the design on screen was made from, for "Edit this design": its text,
// to name the layer, and, for a single line, what the editor's Add Text panel
// needs to make the same design again, so it stays editable there. Several
// lines get no source: that panel takes one line, with no spacing or
// alignment, so editing there would quietly change the design.
interface GeneratedText {
  text: string;
  source?: StoredTextSource;
}

function generatedText(text: string, font: string, widthInches?: number, heightInches?: number): GeneratedText {
  if (text.includes("\n")) return { text };
  return {
    text,
    source: {
      text,
      font,
      ...(widthInches !== undefined ? { widthMm: widthInches * 25.4 } : {}),
      ...(heightInches !== undefined ? { heightMm: heightInches * 25.4 } : {}),
    },
  };
}

// A size box's value in inches, read the way the request reads it: the
// font's own size (undefined) when it's empty or not a number.
function sizeInches(value: string): number | undefined {
  const number = parseFloat(value);
  return Number.isFinite(number) ? number : undefined;
}

export function CreatePage({
  apiUrl,
  token,
  fonts,
  pastAssets,
  onLoadAsset,
  emailCaptureEnabled,
  stitchReplayerEnabled,
  formatConverterEnabled,
  embroideryViewerEnabled,
  shareButtonsEnabled,
  multilineEnabled,
  proUpsellVariant: proUpsellFlag,
}: CreatePageProps) {
  const [text, setText] = useState("Hello World");
  const [selectedFont, setSelectedFont] = useState(fonts.length > 0 && fonts[0] ? fonts[0].name : "");
  const [width, setWidth] = useState("");
  const [height, setHeight] = useState("");
  // Only meaningful once the text is actually more than one line -- see the
  // conditional control below. Left blank rather than defaulted to the
  // engine's own 1.3 so an unset value here really means "don't send
  // line_spacing at all," matching width/height's own optional-field
  // convention and keeping a single-line-equivalent request's content key
  // identical to one that never mentioned spacing.
  const [lineSpacing, setLineSpacing] = useState("");
  // "left" matches what every design already got before this control
  // existed, and (see media.py's content_key) is the one value that never
  // gets hashed into the content key -- so leaving this at its initial
  // value keeps a design's key identical to one that never mentioned
  // alignment at all.
  const [textAlign, setTextAlign] = useState<"left" | "center" | "right">("left");
  const [previewUrl, setPreviewUrl] = useState("");
  const [embroideryFiles, setEmbroideryFiles] = useState<Record<string, string>>({});
  const [dimensions, setDimensions] = useState<{ width: number; height: number } | null>(null);
  // How many stitches the backend's downsampling pass took out of the files
  // (app/services/downsample.py), for analytics. null when unknown: a design
  // reopened from the library rather than generated here.
  const [stitchesRemoved, setStitchesRemoved] = useState<number | null>(null);
  // Whether to ask for downsampling at all: the DownsampleNotice checkbox,
  // shown once a response says it removed stitches. Unticked, every request
  // here sends ?downsample=false.
  const [downsample, setDownsample] = useState(true);
  const generateUrl = (withDownsample: boolean) =>
    `${apiUrl}/api/generate${withDownsample ? "" : "?downsample=false"}`;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showFontSelector, setShowFontSelector] = useState(false);
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };
  const [currentAsset, setCurrentAsset] = useState<Asset | null>(null);
  // The library entry the visible design belongs to. Tracked separately from
  // currentAsset, which is only set when a *past* asset is loaded -- a design
  // generated in this session has an id too, and emailing needs it.
  const [assetId, setAssetId] = useState<string | null>(null);
  // Set alongside embroideryFiles, from what was generated rather than from
  // the form, which may have been edited since.
  const [generated, setGenerated] = useState<GeneratedText | null>(null);

  // Whether there's a design on screen with files to download, which is
  // when the Studio link under the preview is offered: it points at the file
  // they can take there, so without one it would have nothing to say.
  const hasDesignFiles = generated !== null && Object.keys(embroideryFiles).length > 0;
  // The design on screen, for Studio to open with. Absolute, because Studio
  // fetches it from its own origin; apiUrl is empty in production.
  const studioImport = generated && embroideryFiles.pes
    ? { url: new URL(mediaUrl(apiUrl, embroideryFiles.pes), window.location.href).href, name: generated.text }
    : undefined;
  // Either way into Studio from here, the post-download card or that link,
  // ends the card for good (markEditorPromoClicked): they've found it.
  const {
    emailPrompt,
    emailPromptSettled,
    proUpsellVariant,
    notifyDownload,
    showEmailDesignButton,
    openEmailPrompt,
    closeEmailPrompt,
    closeProUpsell,
    markProUpsellInterested,
    editorPromo,
    closeEditorPromo,
    markEditorPromoClicked,
  } = useDownloadPrompts({
    apiUrl,
    token,
    assetId,
    emailCaptureEnabled,
    proUpsellFlag,
    // The post-download Studio card is off for now; the link under the
    // preview still points there.
    editorPromoEnabled: false,
  });

  // Undefined when the app renders without a PostHogProvider, i.e. when
  // BUN_PUBLIC_POSTHOG_KEY is unset (local dev). Call sites must guard.
  const posthog = usePostHog();

  // Fires once per oversized result, not on every re-render: keyed on the
  // width/height values themselves rather than the dimensions object, which
  // gets a new reference on renders that don't change the numbers.
  // embroidery_file_downloaded already logs width_inches on every download,
  // so this alone is enough to check later whether the same person's next
  // download came in under the threshold.
  //
  // font and width_specified read selectedFont/width via closure rather than
  // as effect dependencies -- deliberately. Adding them to the dependency
  // array would refire this on every keystroke in the Width box; leaving
  // them out means it still reads their current value, but only at the
  // moment a new oversized result actually appears, which is what "what was
  // the box set to for the design that triggered this" needs.
  useEffect(() => {
    if (dimensions && dimensions.width > WIDE_HOOP_WARNING_INCHES) {
      posthog?.capture("design_size_warning_shown", {
        width_inches: dimensions.width,
        height_inches: dimensions.height,
        font: selectedFont,
        // Whether the Width box had a value at generation time, vs. being
        // left empty and getting this width from the font's natural size --
        // the two ways this design could have ended up oversized.
        width_specified: width.trim() !== "",
      });
    }
  }, [dimensions?.width, dimensions?.height, posthog]);

  // Canonicalized to the bare path rather than /create/{token}: the token in
  // the URL bar is minted per visitor, so every visitor's URL is technically
  // distinct, and there is nothing about it worth indexing separately.
  usePageMeta({ title: PAGE_TITLE, description: PAGE_DESCRIPTION, path: "/create", structuredData: STRUCTURED_DATA });

  // Load preview from localStorage on mount
  useEffect(() => {
    const savedPreview = localStorage.getItem(`embroidery_preview_${token}`);
    if (savedPreview) {
      try {
        const preview = JSON.parse(savedPreview);
        setText(preview.text || "Hello World");
        setSelectedFont(preview.font || (fonts.length > 0 ? fonts[0].name : ""));
        setWidth(preview.width || "");
        setHeight(preview.height || "");
        setLineSpacing(preview.lineSpacing || "");
        setTextAlign(preview.textAlign || "left");
        if (preview.previewUrl) {
          setPreviewUrl(preview.previewUrl);
        }
        if (preview.dimensions) {
          setDimensions(preview.dimensions);
        }
        if (preview.embroideryFiles) {
          setEmbroideryFiles(preview.embroideryFiles);
        }
        if (preview.assetId) {
          setAssetId(preview.assetId);
        }
        if (preview.text && preview.font) {
          setGenerated(
            generatedText(
              preview.text,
              preview.font,
              sizeInches(preview.width || ""),
              sizeInches(preview.height || ""),
            ),
          );
        }
      } catch (err) {
        console.error("Failed to load saved preview", err);
      }
    }
  }, [token, fonts]);

  // Clear preview from localStorage
  const clearPreviewFromLocalStorage = () => {
    localStorage.removeItem(`embroidery_preview_${token}`);
  };

  // withDownsample is passed by the notice's checkbox, whose new value isn't
  // in state yet when it re-runs the preview.
  const handlePreview = async (withDownsample: boolean = downsample) => {
    if (!text.trim()) {
      setError("Please enter some text");
      return;
    }

    setLoading(true);
    setError("");
    setPreviewUrl("");
    setEmbroideryFiles({});
    setDimensions(null);
    setCurrentAsset(null);

    try {
      const requestBody: any = {
        text: text.trim(),
        font: selectedFont,
        scale: 100,
        token: token,
        preview_only: false,
      };

      if (width) {
        requestBody.width = parseFloat(width);
      }
      if (height) {
        requestBody.scale_height = parseFloat(height);
      }
      if (lineSpacing) {
        requestBody.line_spacing = parseFloat(lineSpacing);
      }
      // Always sent, even at "left" -- harmless (content_key only hashes it
      // when it isn't "left," matching every value this control can't
      // currently produce anyway) and simpler than conditioning on it.
      requestBody.text_align = textAlign;

      const response = await fetch(generateUrl(withDownsample), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to generate preview");
      }

      const data = await response.json();
      if (data.id) setAssetId(data.id);

      let newPreviewUrl = "";
      if (data.png) {
        newPreviewUrl = mediaUrl(apiUrl, data.png);
        setPreviewUrl(newPreviewUrl);
      }

      const formats: Record<string, string> = {};
      Object.keys(data).forEach(key => {
        if (!GENERATE_RESPONSE_FIELDS.has(key)) {
          formats[key] = data[key];
        }
      });
      setEmbroideryFiles(formats);
      setStitchesRemoved(data.stitches_removed ?? null);
      setGenerated(generatedText(text.trim(), selectedFont, sizeInches(width), sizeInches(height)));

      let newDimensions = null;
      if (data.width_inches && data.height_inches) {
        newDimensions = { width: data.width_inches, height: data.height_inches };
        setDimensions(newDimensions);
      }

      if (text.includes("\n")) {
        posthog?.capture("multiline_design_generated", {
          context: "preview",
          font: selectedFont,
          line_count: text.trim().split("\n").length,
          line_spacing: lineSpacing || null,
          text_align: textAlign,
          asset_id: data.id,
        });
      }

      // Save preview to localStorage. assetId rides along with the files:
      // restoring the download buttons without it leaves them working but
      // unable to say which library entry they belong to, which silently
      // disables the email offer after a reload.
      const preview = {
        text: text.trim(),
        font: selectedFont,
        width,
        height,
        lineSpacing,
        textAlign,
        previewUrl: newPreviewUrl,
        dimensions: newDimensions,
        embroideryFiles: formats,
        assetId: data.id,
      };
      localStorage.setItem(`embroidery_preview_${token}`, JSON.stringify(preview));
    } catch (err: any) {
      setError(err.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!text.trim()) {
      setError("Please enter some text");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const requestBody: any = {
        text: text.trim(),
        font: selectedFont,
        scale: 100,
        token: token,
        preview_only: false,
      };

      if (width) {
        requestBody.width = parseFloat(width);
      }
      if (height) {
        requestBody.scale_height = parseFloat(height);
      }
      if (lineSpacing) {
        requestBody.line_spacing = parseFloat(lineSpacing);
      }
      // Always sent, even at "left" -- harmless (content_key only hashes it
      // when it isn't "left," matching every value this control can't
      // currently produce anyway) and simpler than conditioning on it.
      requestBody.text_align = textAlign;

      const response = await fetch(generateUrl(downsample), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to generate embroidery");
      }

      const data = await response.json();
      if (data.id) setAssetId(data.id);

      if (data.png) {
        setPreviewUrl(mediaUrl(apiUrl, data.png));
      }

      const formats: Record<string, string> = {};
      Object.keys(data).forEach(key => {
        if (!GENERATE_RESPONSE_FIELDS.has(key)) {
          formats[key] = data[key];
        }
      });
      setEmbroideryFiles(formats);
      setStitchesRemoved(data.stitches_removed ?? null);
      setGenerated(generatedText(text.trim(), selectedFont, sizeInches(width), sizeInches(height)));

      if (data.width_inches && data.height_inches) {
        setDimensions({ width: data.width_inches, height: data.height_inches });
      }

      if (text.includes("\n")) {
        posthog?.capture("multiline_design_generated", {
          context: "generate",
          font: selectedFont,
          line_count: text.trim().split("\n").length,
          line_spacing: lineSpacing || null,
          text_align: textAlign,
          asset_id: data.id,
        });
      }

      // Clear preview from localStorage since it's now unlocked
      clearPreviewFromLocalStorage();

      // Refresh assets list
      onLoadAsset({} as Asset);
    } catch (err: any) {
      setError(err.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const loadAssetToPreview = (asset: Asset) => {
    setText(asset.text);
    setSelectedFont(asset.font);
    setPreviewUrl(mediaUrl(apiUrl, asset.files.png));
    setDimensions({ width: asset.width_inches, height: asset.height_inches });
    setCurrentAsset(asset);
    setStitchesRemoved(null);
    setAssetId(asset.id);
    // Named by its text, but not editable as text in the editor: what size
    // the library entry was asked for isn't kept, so the editor couldn't
    // make the same design again.
    setGenerated({ text: asset.text });

    // Only show embroidery files if asset is unlocked
    const formats: Record<string, string> = {};
    if (asset.is_unlocked) {
      Object.keys(asset.files).forEach(key => {
        if (key !== 'png' && asset.files[key]) {
          formats[key] = asset.files[key];
        }
      });
    }
    setEmbroideryFiles(formats);
    onLoadAsset(asset);
  };

  const unlockEmbroidery = async (asset: Asset) => {
    setLoading(true);
    setError("");

    try {
      const response = await fetch(generateUrl(downsample), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          text: asset.text,
          font: asset.font,
          scale: 100,
          width: asset.width_inches,
          token: token,
          generated_from: asset.id,
          preview_only: false,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to unlock embroidery files");
      }

      const data = await response.json();
      if (data.id) setAssetId(data.id);

      if (data.png) {
        setPreviewUrl(mediaUrl(apiUrl, data.png));
      }

      const formats: Record<string, string> = {};
      Object.keys(data).forEach(key => {
        if (!GENERATE_RESPONSE_FIELDS.has(key)) {
          formats[key] = data[key];
        }
      });
      setEmbroideryFiles(formats);
      setStitchesRemoved(data.stitches_removed ?? null);

      if (data.width_inches && data.height_inches) {
        setDimensions({ width: data.width_inches, height: data.height_inches });
      }

      // Clear preview from localStorage since it's now unlocked
      clearPreviewFromLocalStorage();

      // Refresh the assets list to show unlocked state
      onLoadAsset(asset);
    } catch (err: any) {
      setError(err.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (format: string, path: string) => {
    posthog?.capture("embroidery_file_downloaded", {
      format,
      font: selectedFont,
      text_length: text.trim().length,
      width_inches: dimensions?.width,
      height_inches: dimensions?.height,
      asset_id: assetId ?? currentAsset?.id,
      from_past_asset: currentAsset !== null,
      stitches_removed: stitchesRemoved,
    });

    const downloadUrl = mediaUrl(apiUrl, path);
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = path.split("/").pop() || `embroidery.${format}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    notifyDownload(format);
    recordExport("create");
  };

  return (
    <>
      <section className="tool-intro">
        <h1>Embroidery Font Generator</h1>
        <p>Create embroidery text online with a free PES file maker. Choose from 100+ embroidery fonts, preview the stitches, and export PES, DST, JEF, or EXP files, right in your browser.</p>
        {embroideryViewerEnabled && (
          <p>
            <small>
              <a href="/preview">Open an embroidery file online →</a>
            </small>
          </p>
        )}
      </section>

      <div className="two-column-layout">
        <div className="column-left">
          <h2 className="column-heading">Create Your Embroidery Text</h2>
          <form onSubmit={(e) => { e.preventDefault(); handleGenerate(); }}>
            <label>
              Text
              {multilineEnabled ? (
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Enter text to embroider"
                  maxLength={100}
                  rows={2}
                  required
                />
              ) : (
                <input
                  type="text"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Enter text to embroider"
                  maxLength={100}
                  required
                />
              )}
            </label>

            <FontSelector
              apiUrl={apiUrl}
              fonts={fonts}
              selectedFont={selectedFont}
              onSelectFont={(fontName) => {
                setSelectedFont(fontName);
                setShowFontSelector(false);
              }}
              showFontSelector={showFontSelector}
              onToggleSelector={() => setShowFontSelector(!showFontSelector)}
              label="Embroidery Font"
            />

            <div className="form-row">
              <label>
                Width (inches) <small className="label-hint">- optional</small>
                <input
                  type="number"
                  step="0.1"
                  min="0.5"
                  max="12"
                  value={width}
                  onChange={(e) => setWidth(e.target.value)}
                  placeholder="Auto"
                />
              </label>

              <label>
                Height (inches) <small className="label-hint">- optional</small>
                <input
                  type="number"
                  step="0.1"
                  min="0.5"
                  max="12"
                  value={height}
                  onChange={(e) => setHeight(e.target.value)}
                  placeholder="Auto"
                />
              </label>
            </div>

            {multilineEnabled && text.includes("\n") && (
              <>
                <label>
                  Line spacing <small className="label-hint">- optional</small>
                  <input
                    type="number"
                    step="0.1"
                    min="0.8"
                    max="3"
                    value={lineSpacing}
                    onChange={(e) => setLineSpacing(e.target.value)}
                    placeholder="1.3"
                  />
                </label>

                <label>
                  Alignment
                  <div role="group" aria-label="Text alignment" style={{ display: "flex", width: "fit-content", marginTop: "0.25rem", marginBottom: "1rem" }}>
                    {(["left", "center", "right"] as const).map((align) => (
                      <button
                        key={align}
                        type="button"
                        className={textAlign === align ? undefined : "outline secondary"}
                        aria-pressed={textAlign === align}
                        onClick={() => setTextAlign(align)}
                        title={`Align ${align}`}
                        style={{ flex: "0 0 auto", width: "auto", padding: "0.5rem 0.9rem" }}
                      >
                        <i className={`fa-solid fa-align-${align}`} aria-hidden="true" />
                      </button>
                    ))}
                  </div>
                </label>
              </>
            )}

            {width && height && (
              <small style={{ display: 'block', marginTop: '-0.5rem', color: 'var(--pico-color-amber-500)' }}>
                ⚠️ Warning: Specifying both width and height may stretch or distort the design.
              </small>
            )}

            <small style={{ display: 'block', marginTop: '-0.5rem', color: 'var(--pico-muted-color)' }}>
              Leave both empty to use the font's original size. Enter a width or height to scale the text proportionally.
            </small>

            <button
              type="button"
              onClick={() => handlePreview()}
              disabled={loading || !text.trim()}
              // The hint above is pulled up by a negative margin to sit close to
              // the inputs it explains; without a margin here the button ends up
              // touching it.
              style={{ width: '100%', marginTop: '1.25rem' }}
            >
              Preview
            </button>
          </form>
        </div>

        <div className="column-right">
          <h2 className="column-heading">Preview & Export</h2>

          {error && (
            <article className="error-message">
              <p><strong>Error:</strong> {error}</p>
            </article>
          )}

          {loading && !previewUrl && <LoadingMessage />}

          {!loading && !previewUrl && !error && (
            <div className="empty-preview">
              <p>Your embroidery preview will appear here</p>
              <small>Fill out the form on the left to generate your design</small>
            </div>
          )}

          {previewUrl && (
            <div className="result-preview">
              <figure className="preview-container">
                <img src={previewUrl} alt="Embroidery preview" />
                {dimensions && (
                  <figcaption style={{ marginTop: "1rem", fontSize: "0.9em", color: "var(--pico-muted-color)" }}>
                    Dimensions: {dimensions.width.toFixed(2)}" × {dimensions.height.toFixed(2)}"
                  </figcaption>
                )}
              </figure>

              {dimensions && dimensions.width > WIDE_HOOP_WARNING_INCHES && (
                <p className="size-warning">
                  This design is {dimensions.width.toFixed(2)}" wide, wider than many hoop
                  sizes. Try a smaller value in the Width box on the left, or resize it on
                  your machine before stitching.
                </p>
              )}

              {stitchReplayerEnabled && assetId && (
                <p style={{ textAlign: "center", marginTop: "0.5rem" }}>
                  <a href={`/replay/${assetId}`}>Stitch Simulator</a>
                  {shareButtonsEnabled && (
                    <>
                      {"  ·  "}
                      <ShareButton path={`/replay/${assetId}`} source="create" />
                    </>
                  )}
                </p>
              )}

              {hasDesignFiles && (
                <p className="studio-upsell" style={{ textAlign: "center", marginTop: "0.5rem" }}>
                  <StudioLink source="create_link" newTab onClick={markEditorPromoClicked} importFile={studioImport}>
                    {/* The arrow kept with "free" so it never wraps onto a line alone. */}
                    Add a logo, shapes or more text in Stitch Studio, free{"\u00a0"}→
                  </StudioLink>
                </p>
              )}

              {Object.keys(embroideryFiles).length === 0 ? (
                <>
                  {currentAsset && !currentAsset.is_unlocked ? (
                    <>
                      <p style={{ marginTop: "1rem", textAlign: "center" }}>
                        Embroidery files are ready. Click below to download them.
                      </p>
                      <button
                        style={{width: "100%", marginTop: "1rem"}}
                        onClick={() => unlockEmbroidery(currentAsset)}
                        disabled={loading}
                      >
                        Generate Embroidery Files
                      </button>
                    </>
                  ) : currentAsset && currentAsset.is_unlocked ? (
                    <>
                      <p style={{ marginTop: "1rem", textAlign: "center" }}>
                        Embroidery files are ready. Click below to re-download them.
                      </p>
                      <button
                        style={{width: "100%", marginTop: "1rem"}}
                        onClick={() => unlockEmbroidery(currentAsset)}
                        disabled={loading}
                      >
                        Load Embroidery Files
                      </button>
                    </>
                  ) : (
                    <>
                      <p style={{ marginTop: "1rem", textAlign: "center" }}>
                        Click below to generate embroidery files for this preview.
                      </p>
                      <button
                        style={{width: "100%", marginTop: "1rem"}}
                        onClick={handleGenerate}
                        disabled={loading}
                      >
                        Generate Embroidery Files
                      </button>
                    </>
                  )}
                </>
              ) : (
                <>
                  <DownsampleNotice
                    stitchesRemoved={stitchesRemoved}
                    enabled={downsample}
                    disabled={loading}
                    onChange={enabled => {
                      setDownsample(enabled);
                      handlePreview(enabled);
                    }}
                  />
                  <FormatDownloadButtons files={embroideryFiles} onDownload={handleDownload} onEmailDesign={showEmailDesignButton ? openEmailPrompt : undefined} />
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Full width, below both columns: the thumbnails were previously
          squeezed into the bottom of the left column while the right one sat
          empty under the download buttons. */}
      <PastAssetsGrid
        assets={pastAssets.filter((asset) => CREATE_ENGINES.includes(asset.engine))}
        apiUrl={apiUrl}
        onSelectAsset={loadAssetToPreview}
      />
      {/* {pastAssets.some((asset) => !CREATE_ENGINES.includes(asset.engine)) && (
        <p style={{ textAlign: "center", color: "var(--pico-muted-color)" }}>
          <small>
            Also have designs from the <a href="/digitize">SVG digitizer</a>?{" "}
            They show up there, not here.
          </small>
        </p>
      )} */}

      <section className="tool-details">
        <h2>What is an embroidery font generator?</h2>
        <p>
          An embroidery font generator turns typed text into stitch data
          for machine embroidery. The fonts above are designed for
          embroidery, so the generator creates the stitch data a
          machine needs to sew the text.
        </p>
        <p>
          That makes it a PES file maker as well. PES is the format
          Brother machines read, and every design generated here
          includes one, so you don't need a separate PES file creator
          or digitizing software to get from typed text to a file your
          machine can stitch.
        </p>

        <h2>How to make a PES file from text</h2>
        <ol>
          <li>
            Type the text you want to stitch, then choose an embroidery
            font. The font picker shows a preview of each font.
          </li>
          <li>
            Leave the width and height fields empty to use the font's
            original size, or enter one dimension to scale the design
            proportionally.
          </li>
          <li>
            Click Preview to check the design and its dimensions.
          </li>
          <li>
            When it looks right, generate the embroidery files and
            download the PES file. DST, JEF, and EXP versions come with
            every design too.
          </li>
        </ol>
        <p>
          Your past designs are saved below so you can re-download them
          later.
        </p>

        <h2>Choose an embroidery font</h2>
        <p>
          Embroidery fonts can differ quite a bit in how they stitch.
          Letter shapes, density, and stitch direction all affect how the
          finished text looks on fabric.
        </p>
        <p>
          The font picker above shows a preview of each option, so you
          can compare a few before choosing one.
        </p>
        {fonts.filter((f) => f.has_preview).length > 0 && (
          <div className="font-examples">
            {fonts
              .filter((f) => f.has_preview)
              .slice(0, 6)
              .map((font) => (
                <img
                  key={font.name}
                  src={mediaUrl(apiUrl, `/fonts/preview/${encodeURI(font.name)}`)}
                  alt={font.name}
                  title={font.name}
                  loading="lazy"
                  className="font-example-img"
                />
              ))}
          </div>
        )}

        <h2>Supported embroidery file formats</h2>
        <p>
          Each design can be downloaded as PES, DST, JEF, or EXP. These
          are embroidery machine file formats that contain the stitch
          instructions for the design.
        </p>
        <ul className="format-list">
          <li><strong>PES</strong> – commonly used by Brother machines</li>
          <li><strong>DST</strong> – widely used by Tajima and commercial machines</li>
          <li><strong>JEF</strong> – commonly used by Janome machines</li>
          <li><strong>EXP</strong> – commonly used by Bernina machines</li>
        </ul>
        <p>
          Need a different format?{" "}
          {formatConverterEnabled ? (
            <a href="/convert">The embroidery file converter</a>
          ) : (
            "The embroidery file converter"
          )}{" "}
          can convert files between PES, DST, JEF, EXP, and VP3.
        </p>

        <h2>Choosing a size for embroidered text</h2>
        <p>
          Embroidery text can behave differently at small sizes depending
          on the font, fabric, thread, and stitch density. There isn't
          one minimum size that works for every design.
        </p>
        <p>
          Use the width and height fields to scale your text and check
          the resulting dimensions in the preview. Designs that are too
          large for common hoop sizes are flagged automatically.
        </p>
        {(stitchReplayerEnabled || embroideryViewerEnabled) && (
          <p>
            {stitchReplayerEnabled && (
              <>
                Use the{" "}
                <a href={assetId ? `/replay/${assetId}` : "/replay"}>
                  stitch simulator
                </a>{" "}
                to see how the stitches are laid out.{" "}
              </>
            )}
            {embroideryViewerEnabled && (
              <>
                If you already have an embroidery file, you can inspect it
                with the <a href="/preview">embroidery file viewer</a>{" "}
                before stitching.
              </>
            )}
          </p>
        )}
      </section>

      <section className="faq-section">
        <h2>Frequently Asked Questions</h2>
        <div className="faq-container">
          {FAQ_DATA.map((faq, index) => (
            <div key={index} className="faq-item">
              <button
                className="faq-question"
                onClick={() => toggleFAQ(index)}
                aria-expanded={openFAQ === index}
                type="button"
              >
                <span>{faq.question}</span>
                <span className={`faq-icon ${openFAQ === index ? 'open' : ''}`}>
                  {openFAQ === index ? '−' : '+'}
                </span>
              </button>
              <div className={`faq-answer ${openFAQ === index ? 'open' : ''}`}>
                <p>{faq.answer}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {emailPrompt && assetId && (
        <EmailDesignModal
          apiUrl={apiUrl}
          token={token}
          assetId={assetId}
          format={emailPrompt.format}
          prefilledEmail={emailPrompt.prefilledEmail}
          requested={emailPrompt.requested}
          availableFormats={Object.keys(embroideryFiles)}
          source="create"
          onClose={closeEmailPrompt}
        />
      )}

      {/* Waits for the design-email modal to close (or to never have opened
          at all) rather than rendering alongside it: both want centered
          screen space, and offsetting one within a typical viewport still
          measurably overlapped the other's controls -- see ProUpsellCard's
          own comment. Sequencing the eligibility check (above, at download
          time) is separate from sequencing the render (here) -- the flag
          read, frequency cap, and "shown at most once a day" all still
          happen exactly at download time either way.

          emailPromptSettled matters here as its own condition, not just
          !emailPrompt: emailPrompt is also null while its own prefill fetch
          is still pending, and without this the Pro card would flash on
          screen during that gap and then vanish the instant the fetch
          resolves and emailPrompt becomes non-null. */}
      {proUpsellVariant && !emailPrompt && emailPromptSettled && (
        <ProUpsellCard
          variant={proUpsellVariant}
          onDone={closeProUpsell}
          onInterested={markProUpsellInterested}
        />
      )}

      {/* Sequenced after the design-email modal for the same reasons as the
          Pro card above. notifyDownload never offers both on one download,
          but a Pro card left open from an earlier one still goes first. */}
      {editorPromo && !proUpsellVariant && !emailPrompt && emailPromptSettled && (
        <EditorPromoCard source="create" onDone={closeEditorPromo} onClicked={markEditorPromoClicked} />
      )}
    </>
  );
}
